/**
 * @module app/cardOrientation
 * @description Decides whether a card renders landscape or portrait.
 *
 * Mode (highest priority first): `?layout=adaptive|landscape` in the page
 * URL, then `VITE_AFJ_CARD_LAYOUT` at build time, then landscape.
 * In adaptive mode a card turns portrait when the viewport is phone-width.
 *
 * `<FixedLandscape>` pins everything inside it to landscape: print output,
 * the print-shop PDF stage and admin previews always show the printed card.
 */
import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { DEFAULT_CARD_LAYOUT, PORTRAIT_MAX_WIDTH_PX, isCardLayoutMode, type CardLayoutMode } from "../config/layout";

export type CardOrientation = "landscape" | "portrait";

/** Pure resolver, so the priority order is testable without a browser. */
export function resolveCardLayout(urlValue: string | null, buildValue: unknown): CardLayoutMode {
    if (isCardLayoutMode(urlValue)) return urlValue;
    if (isCardLayoutMode(buildValue)) return buildValue;
    return DEFAULT_CARD_LAYOUT;
}

export const CARD_LAYOUT: CardLayoutMode = resolveCardLayout(
    new URLSearchParams(window.location.search).get("layout"),
    import.meta.env.VITE_AFJ_CARD_LAYOUT
);

const PHONE_QUERY = `(max-width: ${PORTRAIT_MAX_WIDTH_PX}px)`;

/** Live viewport check; re-renders cards when a phone rotates or a window resizes across the breakpoint. */
function useIsPhoneWidth(): boolean {
    return useSyncExternalStore(
        (listener) => {
            const mq = window.matchMedia(PHONE_QUERY);
            mq.addEventListener("change", listener);
            return () => mq.removeEventListener("change", listener);
        },
        () => window.matchMedia(PHONE_QUERY).matches
    );
}

const FixedContext = createContext(false);

/** Everything inside renders as the printed (landscape) card. */
export function FixedLandscape({ children }: { children: ReactNode }) {
    return <FixedContext.Provider value={true}>{children}</FixedContext.Provider>;
}

export function useCardOrientation(): CardOrientation {
    const fixed = useContext(FixedContext);
    const phone = useIsPhoneWidth();
    return !fixed && CARD_LAYOUT === "adaptive" && phone ? "portrait" : "landscape";
}
